/**
 * Multi-timeframe market metrics + regime engine for SniperBot.
 * Opt-in via cfg.regimeEngineEnabled (default true in defaultBotCfg).
 */

export const REGIMES = {
  BULL_ACTIVE: "BULL_ACTIVE",
  RECOVERY_BREAKOUT: "RECOVERY_BREAKOUT",
  NORMAL_CHOP: "NORMAL_CHOP",
  DEFENSIVE: "DEFENSIVE",
  CAPITAL_PROTECTION: "CAPITAL_PROTECTION",
};

export const PRICE_HISTORY_RETENTION_MS = 25 * 60 * 60 * 1000;
export const REGIME_MIN_HOLD_MS = 5 * 60 * 1000;
export const RECOVERY_BREAKOUT_MIN_HOLD_MS = 15 * 60 * 1000;
export const RECOVERY_BREAKOUT_MAX_HOLD_MS = 45 * 60 * 1000;
export const RECOVERY_BREAKOUT_MIN_BOUNCE_PCT = 0.0035;
export const RECOVERY_BREAKOUT_NO_FRESH_LOW_MS = 10 * 60 * 1000;
export const RECOVERY_GRID_COUNT_WINDOW_MS = 6 * 60 * 60 * 1000;
export const CAPITAL_PROTECTION_COOLDOWN_MS = 90 * 60 * 1000; // 1.5h after full exit

const TF_15M = 15 * 60 * 1000;
const TF_1H = 60 * 60 * 1000;
const TF_4H = 4 * 60 * 60 * 1000;
const TF_24H = 24 * 60 * 60 * 1000;

export const REGIME_LIMITS = {
  [REGIMES.BULL_ACTIVE]: { minUsdcReservePct: 0.2, maxWethExposurePct: 0.8, majorDipPct: 0.0065 },
  [REGIMES.RECOVERY_BREAKOUT]: { minUsdcReservePct: 0.27, maxWethExposurePct: 0.72, majorDipPct: 0.008 },
  [REGIMES.NORMAL_CHOP]: { minUsdcReservePct: 0.27, maxWethExposurePct: 0.68, majorDipPct: 0.0075 },
  [REGIMES.DEFENSIVE]: { minUsdcReservePct: 0.37, maxWethExposurePct: 0.55, majorDipPct: 0.01 },
  [REGIMES.CAPITAL_PROTECTION]: { minUsdcReservePct: 1, maxWethExposurePct: 0, majorDipPct: 1 },
};

export const REGIME_ADAPTIVE = {
  [REGIMES.BULL_ACTIVE]: { baseMove: 0.0022, sellExtra: 0.0005, volMult: 0.11 },
  [REGIMES.RECOVERY_BREAKOUT]: { baseMove: 0.0025, sellExtra: 0.00055, volMult: 0.13 },
  [REGIMES.NORMAL_CHOP]: { baseMove: 0.0024, sellExtra: 0.00055, volMult: 0.12 },
  [REGIMES.DEFENSIVE]: { baseMove: 0.0032, sellExtra: 0.0008, volMult: 0.17 },
  [REGIMES.CAPITAL_PROTECTION]: { baseMove: 0.0035, sellExtra: 0.0009, volMult: 0.2 },
};

const TIMEFRAME_SUFFICIENCY = {
  [TF_15M]: { minSamples: 4, minCoverageMs: 8 * 60 * 1000 },
  [TF_1H]: { minSamples: 6, minCoverageMs: 30 * 60 * 1000 },
  [TF_4H]: { minSamples: 12, minCoverageMs: 2 * 60 * 60 * 1000 },
  [TF_24H]: { minSamples: 24, minCoverageMs: 12 * 60 * 60 * 1000 },
};

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, Number(n) || 0));
}

/**
 * @param {{ t: number, p: number }[]} samples
 * @param {number} windowMs
 * @param {number} now
 */
export function computeTimeframeMetrics(samples, windowMs, now) {
  const windowStart = now - windowMs;
  const inWindow = (samples || []).filter((s) => s.t >= windowStart && s.p > 0);
  const firstAt = Number(inWindow[0]?.t) || 0;
  const lastAt = Number(inWindow[inWindow.length - 1]?.t) || 0;
  const coverageMs = firstAt > 0 && lastAt >= firstAt ? lastAt - firstAt : 0;
  const requirement = TIMEFRAME_SUFFICIENCY[windowMs] || {
    minSamples: 2,
    minCoverageMs: Math.max(0, windowMs * 0.5),
  };
  const sufficient =
    inWindow.length >= requirement.minSamples &&
    coverageMs >= requirement.minCoverageMs;

  if (!sufficient) {
    const last = samples?.length ? samples[samples.length - 1].p : 0;
    return {
      sufficient: false,
      high: last,
      low: last,
      open: last,
      current: last,
      changePct: 0,
      rangePct: 0,
      drawdownFromHighPct: 0,
      bounceFromLowPct: 0,
      positionInRange: 0.5,
      midpoint: last,
      highAt: lastAt,
      lowAt: lastAt,
      sampleCount: inWindow.length,
      coverageMs,
      requiredCoverageMs: requirement.minCoverageMs,
    };
  }

  const prices = inWindow.map((s) => s.p);
  const high = Math.max(...prices);
  const low = Math.min(...prices);
  const open = inWindow[0].p;
  const current = prices[prices.length - 1];
  const changePct = open > 0 ? (current - open) / open : 0;
  const rangePct = low > 0 ? (high - low) / low : 0;
  const drawdownFromHighPct = high > 0 ? (high - current) / high : 0;
  const bounceFromLowPct = low > 0 ? (current - low) / low : 0;
  const positionInRange =
    high > low ? clamp((current - low) / (high - low), 0, 1) : 0.5;
  const midpoint = (high + low) / 2;
  const highAt = inWindow.reduce((at, sample) => sample.p >= high ? sample.t : at, firstAt);
  const lowAt = inWindow.reduce((at, sample) => sample.p <= low ? sample.t : at, firstAt);

  return {
    sufficient: true,
    high,
    low,
    open,
    current,
    changePct,
    rangePct,
    drawdownFromHighPct,
    bounceFromLowPct,
    positionInRange,
    midpoint,
    highAt,
    lowAt,
    sampleCount: inWindow.length,
    coverageMs,
    requiredCoverageMs: requirement.minCoverageMs,
  };
}

export function computeMarketMetrics(samples, now = Date.now()) {
  return {
    m15: computeTimeframeMetrics(samples, TF_15M, now),
    m1h: computeTimeframeMetrics(samples, TF_1H, now),
    m4h: computeTimeframeMetrics(samples, TF_4H, now),
    m24h: computeTimeframeMetrics(samples, TF_24H, now),
    now,
  };
}

function isFresh15mLow(metrics, currentPrice) {
  const m15 = metrics.m15;
  if (!m15.sufficient || !(m15.low > 0)) return false;
  return currentPrice <= m15.low * 1.0002;
}

function hasBounceFrom15mLow(metrics, minPct = 0.002) {
  return metrics.m15.sufficient && metrics.m15.bounceFromLowPct >= minPct;
}

export function computeHigherLowConfirmed(metrics) {
  const m15 = metrics?.m15;
  const m1h = metrics?.m1h;
  const currentPrice = Number(m15?.current) || 0;
  if (!m15?.sufficient || !m1h?.sufficient || !(m15.low > 0) || !(m1h.low > 0)) {
    return false;
  }

  // The recent 15m low must be distinct from and above the broader 1h low.
  // This prevents an ordinary bounce from the same low being labeled a higher low.
  const distinctHigherLow = m15.low >= m1h.low * 1.001;
  return distinctHigherLow && recoveryBreakoutConfirmation(metrics, currentPrice);
}

/** Restore hysteresis / breakout state from persisted position. */
export function regimePrevFromPosition(pos = {}, refPrev = {}) {
  return {
    regime: refPrev.regime || pos.marketRegime || REGIMES.NORMAL_CHOP,
    regimeSince: refPrev.regimeSince || pos.regimeSince || Date.now(),
    recoveryBreakoutActive:
      refPrev.recoveryBreakoutActive ?? !!pos.recoveryBreakoutActive,
    recoveryBreakoutStartedAt:
      refPrev.recoveryBreakoutStartedAt || pos.recoveryBreakoutStartedAt || 0,
    recoveryBreakoutAnchorPrice:
      refPrev.recoveryBreakoutAnchorPrice || pos.recoveryBreakoutAnchorPrice || 0,
  };
}

/** Fields to persist on position (Moralis). */
export function regimeFieldsFromSnap(snap) {
  if (!snap) return {};
  return {
    marketRegime: snap.regime,
    regimeSince: snap.regimeSince,
    recoveryBreakoutActive: !!snap.recoveryBreakoutActive,
    recoveryBreakoutStartedAt: snap.recoveryBreakoutStartedAt || 0,
    recoveryBreakoutAnchorPrice: snap.recoveryBreakoutAnchorPrice || 0,
    lastLocalLowPrice: snap.lastLocalLowPrice || 0,
    lastLocalLowAt: snap.lastLocalLowAt || 0,
    recentFreshLowAt: snap.recentFreshLowAt || 0,
    higherLowConfirmed: !!snap.higherLowConfirmed,
  };
}

function recoveryBreakoutConfirmation(metrics, currentPrice) {
  const m15 = metrics.m15;
  const m1h = metrics.m1h;
  if (!m15.sufficient || !m1h.sufficient) return false;
  const now = Number(metrics.now) || Date.now();
  const lowAt = Number(m15.lowAt) || now;
  const bounceOk = m15.bounceFromLowPct >= RECOVERY_BREAKOUT_MIN_BOUNCE_PCT;
  const momentumOk = currentPrice > m15.midpoint;
  const rangePositionOk = m1h.positionInRange > 0.45;
  const noFreshLowLongEnough = now - lowAt >= RECOVERY_BREAKOUT_NO_FRESH_LOW_MS;
  const notFreshLow = !isFresh15mLow(metrics, currentPrice);
  return bounceOk && momentumOk && rangePositionOk && noFreshLowLongEnough && notFreshLow;
}

function nearLocalLow(metrics) {
  const m4h = metrics.m4h;
  const m24h = metrics.m24h;
  return (
    (m4h.sufficient && m4h.positionInRange < 0.35) ||
    (m24h.sufficient && m24h.drawdownFromHighPct > 0.03)
  );
}

/**
 * Raw regime from metrics (no hysteresis).
 */
export function detectRawRegime({
  metrics,
  currentPrice,
  recentRecoveryGridCount,
  totalPnLPercent,
  recoveryBuyBlockedUntil,
  capitalProtectionBuyBlockedUntil,
}) {
  const now = metrics.now || Date.now();
  const m15 = metrics.m15;
  const m1h = metrics.m1h;
  const m4h = metrics.m4h;
  const m24h = metrics.m24h;

  const onRecoveryCooldown = now < Number(recoveryBuyBlockedUntil || 0);
  const onCapitalCooldown = now < Number(capitalProtectionBuyBlockedUntil || 0);

  if (onCapitalCooldown) {
    return REGIMES.CAPITAL_PROTECTION;
  }

  const confirmedDowntrend =
    (m4h.sufficient && (
      m4h.changePct < -0.005 ||
      m4h.positionInRange < 0.35 ||
      m4h.drawdownFromHighPct > 0.015
    )) ||
    (m24h.sufficient && m24h.changePct < -0.015);

  if (
    (m24h.sufficient && m24h.drawdownFromHighPct > 0.05) ||
    (recentRecoveryGridCount >= 2 && totalPnLPercent <= -0.005 && confirmedDowntrend) ||
    totalPnLPercent <= -0.045
  ) {
    return REGIMES.CAPITAL_PROTECTION;
  }

  const breakoutTrigger =
    nearLocalLow(metrics) &&
    recoveryBreakoutConfirmation(metrics, currentPrice);

  const higherLowOk = nearLocalLow(metrics) && computeHigherLowConfirmed(metrics);

  if (breakoutTrigger || higherLowOk) {
    if (!onRecoveryCooldown) {
      return REGIMES.RECOVERY_BREAKOUT;
    }
  }

  const defensive =
    (m4h.sufficient && m4h.positionInRange < 0.25 && m4h.changePct < 0) ||
    (m4h.sufficient && m4h.drawdownFromHighPct > 0.015) ||
    (m4h.sufficient && m4h.changePct < -0.015) ||
    (m24h.sufficient && m24h.changePct < -0.025) ||
    isFresh15mLow(metrics, currentPrice);

  if (defensive) {
    return REGIMES.DEFENSIVE;
  }

  const bull =
    m4h.sufficient &&
    m4h.changePct > 0.01 &&
    m24h.sufficient &&
    m24h.changePct >= 0 &&
    m4h.positionInRange > 0.5 &&
    !isFresh15mLow(metrics, currentPrice) &&
    !onRecoveryCooldown;

  if (bull) {
    return REGIMES.BULL_ACTIVE;
  }

  const chop =
    m4h.sufficient &&
    m4h.positionInRange >= 0.25 &&
    m4h.positionInRange <= 0.75 &&
    m24h.sufficient &&
    m24h.changePct >= -0.025 &&
    m24h.changePct <= 0.025;

  if (chop) {
    return REGIMES.NORMAL_CHOP;
  }

  return REGIMES.NORMAL_CHOP;
}

/**
 * Apply hysteresis + RECOVERY_BREAKOUT state machine.
 */
export function resolveMarketRegime({
  metrics,
  currentPrice,
  pos = {},
  recentRecoveryGridCount = 0,
  totalPnLPercent = 0,
  prev = {},
}) {
  const now = metrics.now || Date.now();
  const raw = detectRawRegime({
    metrics,
    currentPrice,
    recentRecoveryGridCount,
    totalPnLPercent,
    recoveryBuyBlockedUntil: pos.recoveryBuyBlockedUntil,
    capitalProtectionBuyBlockedUntil: pos.capitalProtectionBuyBlockedUntil,
  });

  let regime = prev.regime || REGIMES.NORMAL_CHOP;
  let regimeSince = prev.regimeSince || now;
  let recoveryBreakoutActive = !!prev.recoveryBreakoutActive;
  let recoveryBreakoutStartedAt = prev.recoveryBreakoutStartedAt || 0;
  let recoveryBreakoutAnchorPrice = prev.recoveryBreakoutAnchorPrice || 0;

  const m15 = metrics.m15;
  const lastLocalLowPrice =
    m15.sufficient && m15.low > 0
      ? m15.low
      : Number(pos.lastLocalLowPrice) || 0;
  const lastLocalLowAt =
    m15.sufficient && Number(m15.lowAt) > 0
      ? Number(m15.lowAt)
      : pos.lastLocalLowAt || 0;
  const recentFreshLowAt = isFresh15mLow(metrics, currentPrice) ? now : pos.recentFreshLowAt || 0;

  const heldMs = now - regimeSince;
  const canSwitch =
    raw === REGIMES.CAPITAL_PROTECTION || heldMs >= REGIME_MIN_HOLD_MS;

  if (recoveryBreakoutActive) {
    const breakoutHeld = now - recoveryBreakoutStartedAt;
    const freshLow = isFresh15mLow(metrics, currentPrice);
    const brokeBelowAnchor =
      recoveryBreakoutAnchorPrice > 0 && currentPrice < recoveryBreakoutAnchorPrice * 0.998;
    const confirmationStillValid = recoveryBreakoutConfirmation(metrics, currentPrice);
    if (raw === REGIMES.CAPITAL_PROTECTION) {
      recoveryBreakoutActive = false;
      recoveryBreakoutStartedAt = 0;
      recoveryBreakoutAnchorPrice = 0;
      regime = REGIMES.CAPITAL_PROTECTION;
      regimeSince = now;
    } else if (freshLow || brokeBelowAnchor) {
      recoveryBreakoutActive = false;
      recoveryBreakoutStartedAt = 0;
      recoveryBreakoutAnchorPrice = 0;
      regime = REGIMES.DEFENSIVE;
      regimeSince = now;
    } else if (breakoutHeld < RECOVERY_BREAKOUT_MIN_HOLD_MS) {
      regime = REGIMES.RECOVERY_BREAKOUT;
    } else if (raw === REGIMES.BULL_ACTIVE) {
      recoveryBreakoutActive = false;
      recoveryBreakoutStartedAt = 0;
      recoveryBreakoutAnchorPrice = 0;
      regime = REGIMES.BULL_ACTIVE;
      regimeSince = now;
    } else if (raw === REGIMES.RECOVERY_BREAKOUT && confirmationStillValid) {
      regime = REGIMES.RECOVERY_BREAKOUT;
    } else if (
      raw === REGIMES.DEFENSIVE ||
      raw === REGIMES.NORMAL_CHOP ||
      breakoutHeld >= RECOVERY_BREAKOUT_MAX_HOLD_MS
    ) {
      recoveryBreakoutActive = false;
      recoveryBreakoutStartedAt = 0;
      recoveryBreakoutAnchorPrice = 0;
      regime = raw === REGIMES.DEFENSIVE ? REGIMES.DEFENSIVE : REGIMES.NORMAL_CHOP;
      regimeSince = now;
    } else {
      regime = REGIMES.RECOVERY_BREAKOUT;
    }
  } else if (canSwitch) {
    if (raw === REGIMES.RECOVERY_BREAKOUT) {
      recoveryBreakoutActive = true;
      recoveryBreakoutStartedAt = now;
      recoveryBreakoutAnchorPrice = m15.sufficient ? m15.low : currentPrice;
      regime = REGIMES.RECOVERY_BREAKOUT;
      regimeSince = now;
    } else if (raw !== regime) {
      regime = raw;
      regimeSince = now;
    }
  }

  const limits = REGIME_LIMITS[regime] || REGIME_LIMITS[REGIMES.NORMAL_CHOP];
  const adaptive = REGIME_ADAPTIVE[regime] || REGIME_ADAPTIVE[REGIMES.NORMAL_CHOP];
  const higherLowConfirmed = computeHigherLowConfirmed(metrics);

  return {
    regime,
    rawRegime: raw,
    regimeSince,
    recoveryBreakoutActive,
    recoveryBreakoutStartedAt,
    recoveryBreakoutAnchorPrice,
    lastLocalLowPrice: Number.isFinite(lastLocalLowPrice) ? lastLocalLowPrice : m15.low || 0,
    lastLocalLowAt,
    recentFreshLowAt,
    higherLowConfirmed,
    metrics,
    limits,
    adaptive,
    fresh15mLow: isFresh15mLow(metrics, currentPrice),
    bounce15mPct: m15.bounceFromLowPct,
    bounce1hPct: metrics.m1h.bounceFromLowPct,
  };
}

export function applyRegimeToAdaptiveMoves(baseCfg, regimeSnap, oneHourRangePct, clampNumber) {
  const clamp = clampNumber || ((v, lo, hi) => Math.min(hi, Math.max(lo, Number(v) || 0)));
  const adj = regimeSnap?.adaptive || REGIME_ADAPTIVE[REGIMES.NORMAL_CHOP];
  const baseMove = clamp(adj.baseMove, 0.0001, 0.02);
  const sellExtra = clamp(adj.sellExtra, 0, 0.02);
  const volMultiplier = clamp(adj.volMult, 0, 2);
  const minMove = clamp(baseCfg.adaptiveMinMovePct, 0.0001, 0.02);
  const maxMove = clamp(baseCfg.adaptiveMaxMovePct, minMove, 0.05);
  const buyMove = clamp(baseMove + oneHourRangePct * volMultiplier, minMove, maxMove);
  const sellMove = clamp(buyMove + sellExtra, minMove, maxMove + sellExtra);
  return { buyMove, sellMove, baseMove, sellExtra, volMultiplier };
}

/**
 * Regime gates layered on top of buy protection (no cooldown bypass).
 */
export function evaluateRegimeBuyBlocks({
  regimeSnap,
  currentPrice,
  consecBuys,
  book,
  plannedBuyUsd,
  pos = {},
}) {
  const blocks = [];
  const regime = regimeSnap?.regime || REGIMES.NORMAL_CHOP;
  const limits = regimeSnap?.limits || REGIME_LIMITS[REGIMES.NORMAL_CHOP];
  const metrics = regimeSnap?.metrics;
  const now = Date.now();

  if (now < Number(pos.capitalProtectionBuyBlockedUntil || 0)) {
    const mins = Math.ceil((pos.capitalProtectionBuyBlockedUntil - now) / 60000);
    blocks.push({
      kind: "capitalExitCooldown",
      message: `Post full-exit buy cooldown: ${mins}m remaining`,
    });
    return { blocked: true, blocks };
  }

  if (regime === REGIMES.CAPITAL_PROTECTION) {
    blocks.push({ kind: "capitalProtection", message: "Capital protection — buys blocked" });
    return { blocked: true, blocks };
  }

  const total = book?.totalBookUsd || 0;
  const base = book?.baseBalance || 0;
  const holdingsUsd = book?.holdingsUsd || 0;
  if (total > 0) {
    const wethPct = holdingsUsd / total;
    const usdcPct = base / total;
    const planned = Math.min(base, Math.max(0, Number(plannedBuyUsd) || 0));
    // A BUY converts existing USDC into WETH; it does not increase book value.
    const afterWethPct = (holdingsUsd + planned) / total;
    const afterUsdcPct = Math.max(0, base - planned) / total;

    if (limits.maxWethExposurePct < 1 && afterWethPct > limits.maxWethExposurePct + 0.001) {
      blocks.push({
        kind: "maxWethExposure",
        message: `Max WETH exposure ${(limits.maxWethExposurePct * 100).toFixed(0)}% (would be ${(afterWethPct * 100).toFixed(1)}%)`,
      });
    }
    if (afterUsdcPct < limits.minUsdcReservePct - 0.001) {
      blocks.push({
        kind: "minUsdcReserve",
        message: `USDC reserve min ${(limits.minUsdcReservePct * 100).toFixed(0)}% (would be ${(afterUsdcPct * 100).toFixed(1)}%)`,
      });
    }
    if (!blocks.length && wethPct > limits.maxWethExposurePct + 0.001) {
      blocks.push({
        kind: "maxWethExposure",
        message: `WETH exposure ${(wethPct * 100).toFixed(1)}% > max ${(limits.maxWethExposurePct * 100).toFixed(0)}%`,
      });
    }
    if (!blocks.length && usdcPct < limits.minUsdcReservePct - 0.001) {
      blocks.push({
        kind: "minUsdcReserve",
        message: `USDC reserve ${(usdcPct * 100).toFixed(1)}% < min ${(limits.minUsdcReservePct * 100).toFixed(0)}%`,
      });
    }
  }

  const freshLow = regimeSnap?.fresh15mLow;
  if (freshLow && metrics) {
    const allowFreshLowBuy =
      regime === REGIMES.BULL_ACTIVE &&
      metrics.m4h?.sufficient &&
      metrics.m4h.changePct > 0 &&
      consecBuys < 2;
    const bounceOk = hasBounceFrom15mLow(metrics, 0.002);
    const breakout = regime === REGIMES.RECOVERY_BREAKOUT && recoveryBreakoutConfirmation(metrics, currentPrice);

    if (!allowFreshLowBuy && !bounceOk && !breakout) {
      blocks.push({
        kind: "fresh15mLow",
        message: "Fresh 15m low — need bounce confirmation or recovery breakout",
      });
    }
  }

  if (regime === REGIMES.DEFENSIVE && consecBuys >= 2 && !regimeSnap?.recoveryBreakoutActive) {
    blocks.push({
      kind: "defensiveRegime",
      message: "Defensive regime — consecutive buys limited (3+ blocked)",
    });
  }

  return { blocked: blocks.length > 0, blocks };
}

export function getMajorDipPctForRegime(regime) {
  return (REGIME_LIMITS[regime] || REGIME_LIMITS[REGIMES.NORMAL_CHOP]).majorDipPct;
}

/**
 * Sell-side regime adjustments (never blocks SELL entirely except kill switch elsewhere).
 */
export function evaluateRegimeSellAdjustments({
  regimeSnap,
  book,
  totalPnLPercent = 0,
  baseSellMultiplier = 0.3,
}) {
  const notes = [];
  let sellMultiplierFactor = 1;
  let maxSellFractionOfHoldings = null;

  if (!regimeSnap?.metrics) {
    return { sellMultiplierFactor, maxSellFractionOfHoldings, encourageSell: false, notes };
  }

  const regime = regimeSnap.regime;
  const m1h = regimeSnap.metrics.m1h;
  const m4h = regimeSnap.metrics.m4h;
  const total = book?.totalBookUsd || 0;
  const wethPct = total > 0 ? (book.holdingsUsd || 0) / total : 0;
  const targetWeth = regimeSnap.limits?.maxWethExposurePct ?? 0.68;

  if (regime === REGIMES.RECOVERY_BREAKOUT && regimeSnap.recoveryBreakoutActive) {
    if (wethPct > targetWeth + 0.05) {
      sellMultiplierFactor = 1.12;
      notes.push("Breakout: elevated WETH — encouraging trim");
    } else {
      maxSellFractionOfHoldings = Math.min(baseSellMultiplier * 1.2, 0.45);
      notes.push("Breakout: partial sells only (keep upside participation)");
    }
  }

  if (m1h.sufficient && m1h.positionInRange > 0.8 && wethPct > targetWeth) {
    sellMultiplierFactor = Math.max(sellMultiplierFactor, 1.15);
    notes.push("1h upper range + high WETH — take profit");
  }

  if (m4h.sufficient && m4h.positionInRange > 0.75 && totalPnLPercent > 0) {
    sellMultiplierFactor = Math.max(sellMultiplierFactor, 1.08);
    notes.push("4h upper range + positive PnL — de-risk");
  }

  return {
    sellMultiplierFactor,
    maxSellFractionOfHoldings,
    encourageSell: sellMultiplierFactor > 1,
    notes,
  };
}
